import { useCallback, useEffect, useState } from 'react';
import { Col, Row, Steps } from 'antd';
import { VscAccount, VscMap } from 'react-icons/vsc';
import { IoDocumentsOutline } from 'react-icons/io5';
import { PiIdentificationCardLight } from 'react-icons/pi';
import { FormThemeProvider } from 'theme/FormThemeProvider';
import InformationsStep from './steps/InformationsStep';
import AddressStep from './steps/AddressStep';
import DocumentsStep from './steps/DocumentsStep';
import { StepsContainer } from './styles';
import { BaseRepresentative, Representative } from 'types/Representative';
import IdentificationStep from './steps/IdentificationStep';
import { Modal } from 'components/Modal';
import { ConfirmationModal } from 'components/ConfirmationModal';
import Confirmation from 'components/Icons/Confirmation';
import { TextS } from 'components/Text';

interface IRepresentativesCreationProps {
	isOpen: boolean;
	representative?: BaseRepresentative;
	isLegalRepresentative?: boolean;
	onClose: () => void;
	onCreate: (representative: BaseRepresentative) => void;
	isEdition: boolean;
}

const NaturalPersonRepresentativesCreation = ({
	representative: editRepresentative,
	isOpen,
	isLegalRepresentative,
	onClose,
	onCreate,
	isEdition,
}: IRepresentativesCreationProps) => {
	const [confirmModalOpen, setConfirmModal] = useState<boolean>(false);
	const [currentStep, setCurrentStep] = useState(0);
	const [representative, setRepresentative] = useState<Representative>(
		{} as Representative,
	);

	useEffect(() => {
		if (isOpen) {
			setCurrentStep(0);
			if (editRepresentative) {
				setRepresentative(editRepresentative);
				return;
			}
			setRepresentative({} as Representative);
		}
	}, [isOpen, editRepresentative]);

	const onNext = useCallback(
		(values: Representative) => {
			setRepresentative({
				...((representative ?? {}) as Representative),
				...values,
			});
			setCurrentStep(currentStep + 1);
		},
		[currentStep, representative],
	);

	const onBack = useCallback(
		(values: Representative) => {
			setRepresentative({
				...((representative ?? {}) as Representative),
				...values,
			});
			setCurrentStep(currentStep - 1);
		},
		[currentStep, representative],
	);

	const onFinish = useCallback(
		(values: Representative) => {
			onCreate({
				...((representative ?? {}) as Representative),
				...values,
			});
			setConfirmModal(true);
		},
		[onCreate, representative],
	);

	return (
		<>
			<Modal
				title={
					!!isLegalRepresentative
						? 'Adicionar Representante'
						: 'Adicionar Procurador'
				}
				isOpen={isOpen}
				onClose={onClose}
				width={1000}
			>
				<StepsContainer>
					<Steps
						current={currentStep}
						className="steps"
						direction="vertical"
						items={[
							{
								title: 'Informações',
								icon: <VscAccount />,
							},
							{
								title: 'Identificação',
								icon: <PiIdentificationCardLight />,
							},
							{
								title: 'Endereço',
								icon: <VscMap />,
							},
							{
								title: 'Documentos',
								icon: <IoDocumentsOutline />,
							},
						]}
					/>
					<FormThemeProvider>
						{currentStep === 0 && (
							<InformationsStep
								isLegalRepresentative={isLegalRepresentative}
								representative={representative}
								onNext={onNext}
							/>
						)}
						{currentStep === 1 && (
							<IdentificationStep
								isLegalRepresentative={isLegalRepresentative}
								representative={representative}
								onNext={onNext}
								onBack={onBack}
							/>
						)}
						{currentStep === 2 && (
							<AddressStep
								representative={representative}
								onNext={onNext}
								onBack={onBack}
							/>
						)}
						{currentStep === 3 && (
							<DocumentsStep
								isLegalRepresentative={isLegalRepresentative}
								representative={representative}
								onNext={onFinish}
								onBack={onBack}
							/>
						)}
					</FormThemeProvider>
				</StepsContainer>
			</Modal>
			<ConfirmationModal
				title={
					!!isLegalRepresentative
						? `Representante ${isEdition ? 'editado' : 'adicionado'}`
						: `Procurador ${isEdition ? 'editado' : 'adicionado'}`
				}
				isOpen={confirmModalOpen}
				onClose={() => {
					setConfirmModal(false), onClose();
				}}
				confirmText="Ok, obrigado"
				onConfirm={() => {
					setConfirmModal(false), onClose();
				}}
			>
				<Row
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<Col span={3}>
						<Confirmation />
					</Col>
					<Col span={21}>
						<TextS>{`${!!isLegalRepresentative ? 'Representante' : 'Procurador'} foi ${isEdition ? 'alterado' : 'adicionado'} com sucesso.`}</TextS>
					</Col>
				</Row>
			</ConfirmationModal>
		</>
	);
};

export default NaturalPersonRepresentativesCreation;
